// room.* — entry, state sync, reconnection.
//
// (protocol-events.md §2)
import { z } from 'zod';
import { MAX_DISPLAY_NAME_LENGTH, MAX_ENTRY_PASSWORD_LENGTH, MIN_DISPLAY_NAME_LENGTH, MIN_ENTRY_PASSWORD_LENGTH, } from '../constants.js';
import { customCursorSchema } from '../shared/cursor.js';
import { deviceKindSchema } from '../shared/device.js';
import { layoutKindSchema } from '../shared/layout.js';
import { roomMemberSchema } from '../shared/member.js';
// ─── shared sub-schemas ────────────────────────────────────────────────────
/**
 * Snapshot reference returned by `room.welcome` and `room.resume-ok`.
 *
 * `metaUrl` points at a presigned GET URL for the snapshot's meta.json.
 *
 * `layerUrls` carries fresh presigned GET URLs for each per-layer PNG, signed
 * at welcome / resume-ok time alongside `metaUrl`. The historical design baked
 * the layer URLs into meta.json itself, but presigned URLs expire (default
 * TTL 60 s) — meta.json's URLs were minted at publish time so any client
 * that joined more than 60 s after the last snapshot saw expired URLs and
 * the client would 403 on layer fetch. Re-signing on every welcome / resume
 * sidesteps that without an extra S3 round-trip (the server already holds
 * the layer ids in `Room.snapshotMeta.layers`). meta.json now carries only
 * `{ lastSeq, layers }`; clients MUST use this `layerUrls` array and ignore
 * any `layerUrls` field that may still appear inside meta.json. (architecture.md
 * §14, protocol-events.md §2.3)
 */
export const snapshotRefSchema = z.object({
    metaUrl: z.url(),
    /** lastSeq baked into the snapshot. */
    seq: z.number().int().nonnegative(),
    /**
     * Fresh presigned GET URLs for each per-layer PNG. Order matches
     * `meta.json.layers[]` by `id` but clients should still index by `id`
     * rather than position. Always present; empty array is invalid (a
     * snapshot has at least one layer — MVP has exactly one).
     */
    layerUrls: z.array(z.object({
        id: z.number().int().nonnegative(),
        url: z.url(),
    })),
});
/**
 * Capability negotiation field shared by `room.join.clientFeatures` and
 * `room.welcome.serverFeatures`. MVP doesn't act on any of these; they
 * exist to avoid a schema bump when post-MVP features need negotiation.
 * (protocol.md §7.2)
 */
const featuresSchema = z
    .object({
    binaryFrames: z.boolean().optional(),
    offscreenCanvas: z.boolean().optional(),
    snapshotFormat: z.enum(['png', 'webp']).optional(),
})
    .loose();
/**
 * 入室「あいことば」(owner-vs-entry-password-2026-06-13.md).
 *
 * `rooms.entry_pw_hash` が設定された部屋に visitor で入るときだけ必要.
 * `credential` / `admin` 認証 (= 自分の鍵を持っている人) はあいことばを
 * 打たないので、フィールドは visitor auth にのみ存在する.
 *
 * - 部屋があいことばを要求しないときは無視される (送っても害なし).
 * - 要求する部屋で欠落 / 不一致 → `room.rejected { reason:
 *   'invalid-entry-password' }` + close. 欠落と不一致は区別しない (欠落は
 *   古い client か lookup 結果の取りこぼしで、UI の対処は同じ「入力欄を出す」).
 * - 照合は dispatcher `POST /internal/auth/entry-password` (argon2id) で行う.
 */
const visitorAuthSchema = z.object({
    mode: z.literal('visitor'),
    displayName: z.string().min(MIN_DISPLAY_NAME_LENGTH).max(MAX_DISPLAY_NAME_LENGTH),
    entryPassword: z
        .string()
        .min(MIN_ENTRY_PASSWORD_LENGTH)
        .max(MAX_ENTRY_PASSWORD_LENGTH)
        .optional(),
});
const credentialAuthSchema = z.object({
    mode: z.literal('credential'),
    loginId: z.string().min(1),
    password: z.string().min(1),
});
/**
 * Phase 7.5: オーナー WS 認証経路 (案 P + 案 B 確定、implementation-plan.md
 * §Phase 7.5 / consult 2026-05-05).
 *
 * ワンクリック作成オーナーが `localStorage` に持っている `admin_token` を
 * `room.join` 時に提示する経路. server (`JoinService`) は dispatcher の
 * `POST /internal/auth/admin-token` (shared secret) で照合し、一致なら
 * `permissions = ALL_PERMS` を welcome に載せる. token は **bearer 相当**
 * なので空文字のみ拒否、形式チェックは dispatcher 側 DB 照合で行う.
 *
 * - 一致しない (DB に存在しない / 別ルームの token) → `room.rejected
 *   { reason: 'invalid-admin-token' }` で reject (両ケースを同 reason に
 *   集約、区別は dispatcher の log で十分).
 * - 一致 → ALL_PERMS 付与、displayName は `auth.displayName` を使う
 *   (visitor と同流儀).
 *
 * security: token の流出 (XSS) は architecture.md §18.0 確定方針 T で
 * 受容済 (XSS 対策が前提). サーバ側で client を信用せず必ず DB 照合する.
 */
const adminAuthSchema = z.object({
    mode: z.literal('admin'),
    /** UUIDv7 plaintext (rooms.admin_token と直接照合). 形式は dispatcher 側で検証. */
    adminToken: z.string().min(1),
    displayName: z.string().min(MIN_DISPLAY_NAME_LENGTH).max(MAX_DISPLAY_NAME_LENGTH),
});
const roomAuthSchema = z.discriminatedUnion('mode', [
    visitorAuthSchema,
    credentialAuthSchema,
    adminAuthSchema,
]);
// ─── room.join (c → s) ──────────────────────────────────────────────────────
/**
 * `room.join` — initial entry. Auth payload is carried inside the join
 * itself rather than HTTP headers so visitor (cookie-less) and credential
 * users share the same wire flow.
 * (protocol-events.md §2.1 / §2.2)
 */
export const roomJoinSchema = z.object({
    t: z.literal('room.join'),
    roomId: z.uuid(),
    auth: roomAuthSchema,
    isRom: z.boolean().optional(),
    /** Telemetry only — do NOT use this for protocol gating. */
    clientBuild: z.string().optional(),
    clientFeatures: featuresSchema.optional(),
    /**
     * Telemetry only — analytics layer で HMAC fingerprint 化される
     * (`OEKAKI_ANALYTICS_SALT` 経由). 生値は DB / log / metric に書かれない.
     * PII を含めないこと.
     *
     * web 初回起動時に `crypto.randomUUID()` で発行し `localStorage.anonUid`
     * に永続保存した同一ユーザ識別子. optional な理由は古い client / Storage
     * 不可な環境 (Incognito 厳格モード等) で protocol を壊さないため. 不在時
     * はサーバ側で `HMAC(salt, "anon:" + welcomeUid)` のセッション限定 ID に
     * fallback する (analytics.md §1 / §3.1).
     */
    clientAnonId: z.uuid().optional(),
    /**
     * Telemetry only — analytics layer で raw 値のまま保管される (短い slug
     * 想定、PII 含めない). `?ref=...` URL クエリの値を web が pickup する.
     *
     * 招待リンク経由判別用 (= ファネル「招待 → 入室 → 描画 → リピート」の
     * 入口計測). 64 文字 cap は slug 想定の上限 + 長文 referrer URL や PII の
     * 流入防止 (analytics.md §3.1 / §8).
     */
    referrer: z.string().max(64).optional(),
    /**
     * Telemetry only — レイアウトの利用計測 (pen-usage-analytics-2026-08-18.md §9)。
     * 入室時点で client が表示していた Classic / Modern レイアウト. `clientBuild`
     * と同じ流儀の optional field: 古い client (本フィールド未実装) では欠落し、
     * server は analytics `session.start.layout` に `null` を記録する
     * (protocol は壊れない).
     *
     * server はこの値で分岐しない (= 判定に使わない、記録するだけ). 表示分岐は
     * 常にクライアント側の `LayoutKind` 選択 (per-user, engine/protocol と非依存)
     * が行う.
     */
    layout: layoutKindSchema.optional(),
    /**
     * Telemetry only — 端末区分の計測 (pen-usage-analytics-2026-08-18.md §11.2 /
     * §11.3)。入室時点で client が表示していたシェル + primary pointer:
     *   - `phone`   : フォンシェル表示中 (`isPhoneViewport` が true).
     *   - `tablet`  : coarse pointer 主体 (`(pointer: coarse)`) かつ phone でない.
     *   - `desktop` : それ以外 (primary pointer が fine).
     * `layout` と同じ流儀の optional field: 古い client (本フィールド未実装) では
     * 欠落し、server は analytics `session.start.device` / `session.end.device`
     * に `null` を記録する (protocol は壊れない).
     *
     * server はこの値で分岐しない (= 判定に使わない、記録するだけ. `layout` と
     * 同じ telemetry-only の位置付け).
     */
    device: deviceKindSchema.optional(),
    /**
     * Telemetry only — `window.devicePixelRatio` の計測 (canvas-dot-by-dot-2026-10-28.md
     * 「記録」)。ブラウザのズーム / OS のスケーリングで 101〜199% に落ちている
     * 利用者の割合を見るためのもの。`layout` / `device` と同じ流儀の optional
     * field: 古い client では欠落し、server は analytics `session.start.dpr` に
     * `null` を記録する。server はこの値で分岐しない (記録するだけ)。
     */
    dpr: z.number().positive().max(10).optional(),
    /**
     * Telemetry only — 「ドットバイドット表示」設定 (`auto` | `off`、端末ごとの
     * localStorage `oekaki:dot-by-dot`) の計測。「しない」に切り替えた人の数を
     * 見るためのもの。古い client では欠落し、server は analytics
     * `session.start.dot_by_dot` に `null` を記録する。将来の未知の値は
     * `.catch(undefined)` で欠測扱い (入室を落とさない)。server は分岐しない。
     */
    dotByDot: z.enum(['auto', 'off']).optional().catch(undefined),
});
// ─── room.welcome (s → c) ───────────────────────────────────────────────────
/**
 * `room.welcome` — successful join response.
 *
 * `snapshot` is `null` (not omitted) when the room has just been created
 * and no snapshot has been generated yet — this matches the on-the-wire
 * shape we want clients to handle (`"snapshot": null`).
 * (consult-decided, protocol-events.md §2.3)
 */
export const roomWelcomeSchema = z.object({
    t: z.literal('room.welcome'),
    uid: z.uuid(),
    resumeToken: z.string().min(1),
    displayName: z.string().min(MIN_DISPLAY_NAME_LENGTH).max(MAX_DISPLAY_NAME_LENGTH),
    permissions: z.number().int().nonnegative(),
    isRom: z.boolean(),
    /**
     * Phase 6 Task 5: credential 経由 join のときに dispatcher から返って
     * きた `room_credentials.id` を載せる. visitor 経由では absent. 将来
     * `admin.kick` で「どの credential を使った人か」を識別する用途を残す
     * ためだけのフィールドで、protocol 上の意味解釈は不要 (= reserved).
     */
    credentialId: z.string().min(1).optional(),
    room: z.object({
        id: z.uuid(),
        name: z.string(),
        settings: z.object({
            canvasSize: z.object({
                width: z.number().int().positive(),
                height: z.number().int().positive(),
            }),
        }),
        /**
         * Phase 7 Task 7-5: `whiteboard.clear` を **誰でも** 実行できるか.
         *
         * - `true`  : 無権限 visitor も clear 可能 (旧版風オープン運用).
         * - `false` : `Perm.CLEAR` 保有者のみ clear 可能 (default、既存互換).
         *
         * 判定は server (`WhiteboardService.handleClear`) が `clearOpen ||
         * hasPerm(Perm.CLEAR)` で行う. UI 側 (Task 7-9 で組み込む `/clear` slash
         * command) は本フラグを使って事前にボタンを enable / disable できる.
         */
        clearOpen: z.boolean(),
    }),
    members: z.array(roomMemberSchema),
    /** `null` when no snapshot exists yet (brand-new room). */
    snapshot: snapshotRefSchema.nullable(),
    /**
     * Drawing-only events accumulated since the snapshot.
     * (member-joined / member-left are NOT in welcome's deltaEvents — see
     * protocol-events.md §2.10.)
     *
     * Typed loosely as `unknown[]` here; consumers re-validate items with
     * the appropriate `ServerEvent` discriminator before applying.
     */
    deltaEvents: z.array(z.unknown()),
    lastSeq: z.number().int().nonnegative(),
    serverFeatures: featuresSchema.optional(),
});
// ─── room.rejected (s → c) ──────────────────────────────────────────────────
/** `room.rejected` — join or resume failure. */
export const roomRejectedSchema = z.object({
    t: z.literal('room.rejected'),
    reason: z.enum([
        'invalid-credential',
        /**
         * Phase 7.5: `auth.mode='admin'` の admin_token が DB に存在しない /
         * 一致するが別ルームの token (= roomId 不一致) のいずれか. dispatcher
         * 側の `requireRoomAdmin.ts:12-16` 流儀と同じく、log で区別はするが
         * client 側は同じ reason を受ける (UI 表示は「ルームオーナーとして
         * の認証に失敗しました」).
         */
        'invalid-admin-token',
        /**
         * あいことば付き部屋 (`rooms.entry_pw_hash` あり) に visitor で入ろうとして
         * `auth.entryPassword` が欠落 / 不一致. client は入室画面に留まり (= 戻し)
         * あいことば欄の下にエラーを出して再入力させる. server は rejected 直後に
         * close するので、再試行は fresh connect + `room.join`.
         */
        'invalid-entry-password',
        'kicked',
        'banned',
        'room-not-found',
        'room-deleted',
        'maintenance',
        'rate-limited',
        'resume-expired',
        /** lastSeq > currentSeq, etc. — see protocol-events.md §2.8. */
        'invalid-resume',
        'internal-error',
    ]),
    message: z.string().optional(),
});
// ─── room.member-joined / member-left (s → c, broadcast) ────────────────────
export const roomMemberJoinedSchema = z.object({
    t: z.literal('room.member-joined'),
    uid: z.string(),
    displayName: z.string().min(MIN_DISPLAY_NAME_LENGTH).max(MAX_DISPLAY_NAME_LENGTH),
    isRom: z.boolean(),
    /**
     * custom-cursor-2026-09-19.md §2.3: always `null`/absent on a fresh join
     * (server sets `Member.cursor = null` at join time; the client sends its
     * own `member.cursor-change` after `room.welcome`). Non-null values only
     * appear on the "rejoin after suppression timer fired" broadcast in
     * `ResumeService`, where the member's prior cursor is carried over.
     * Optional for backward compatibility with older servers.
     */
    cursor: customCursorSchema.nullable().optional(),
    seq: z.number().int().nonnegative(),
    ts: z.number().int().nonnegative(),
});
export const roomMemberLeftSchema = z.object({
    t: z.literal('room.member-left'),
    uid: z.string(),
    /**
     * Distinguishes "left voluntarily", "kicked by an admin/credential", and
     * "WS dropped" so the chat UI can surface different copy.
     */
    reason: z.enum(['leave', 'kick', 'disconnect']),
    /**
     * `uid` of the actor who issued `admin.kick` against `uid`. Only meaningful
     * (and only stamped server-side) when `reason === 'kick'`. Lets the chat
     * UI render「{kicker} が {target} をキックしました」without burning a
     * separate broadcast event (Phase 7 Task 7-4 確定方針 / protocol-events.md
     * §2.6).
     *
     * Optional for backward compatibility — older servers omit it and clients
     * fall back to the kicker-less wording.
     */
    kickerUid: z.string().optional(),
    seq: z.number().int().nonnegative(),
    ts: z.number().int().nonnegative(),
});
// ─── room.resume (c → s) ────────────────────────────────────────────────────
/**
 * `room.resume` — reconnect. Replays any seq strictly greater than
 * `lastSeq`. May be promoted to a snapshot fallback if the session
 * buffer no longer covers `lastSeq`.
 */
export const roomResumeSchema = z.object({
    t: z.literal('room.resume'),
    roomId: z.uuid(),
    uid: z.uuid(),
    resumeToken: z.string().min(1),
    lastSeq: z.number().int().nonnegative(),
});
// ─── room.resume-ok (s → c) ─────────────────────────────────────────────────
//
// `room.resume-ok` carries one of two shapes depending on whether the
// session buffer can serve the gap or a snapshot fallback is required.
// We model it as ONE outer `t: 'room.resume-ok'` schema whose inner
// `mode` field is itself a discriminator over `delta` | `snapshot`.
// (consult-decided)
//
// Each branch has the common "envelope" fields (`uid`, `permissions`,
// `members`, `lastSeq`) plus mode-specific cargo.
const roomResumeOkCommon = {
    t: z.literal('room.resume-ok'),
    uid: z.uuid(),
    permissions: z.number().int().nonnegative(),
    members: z.array(roomMemberSchema),
    lastSeq: z.number().int().nonnegative(),
};
const roomResumeOkDeltaSchema = z.object({
    ...roomResumeOkCommon,
    mode: z.literal('delta'),
    /** Events with seq > request.lastSeq, in seq order. */
    events: z.array(z.unknown()),
});
const roomResumeOkSnapshotSchema = z.object({
    ...roomResumeOkCommon,
    mode: z.literal('snapshot'),
    snapshot: snapshotRefSchema,
    deltaEvents: z.array(z.unknown()),
});
/**
 * Full `room.resume-ok` schema. `t` is fixed but the inner `mode`
 * picks the right cargo set.
 */
export const roomResumeOkSchema = z.discriminatedUnion('mode', [
    roomResumeOkDeltaSchema,
    roomResumeOkSnapshotSchema,
]);
//# sourceMappingURL=room.js.map